import {
  AbstractControl,
  FormGroup,
  NonNullableFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { objectValidator, objectValidatorOptional, requiredWithEmptyArray } from '@libs/core/lib/form-controls/custom';
import {
  Entity,
  addressCreateSchema,
  addressUpdateSchema,
  attachmentCreateSchema,
  attachmentUpdateSchema,
  categoryCreateSchema,
  categoryUpdateSchema,
  chatMessageCreateSchema,
  chatMessageReadCreateSchema,
  chatMessageReadUpdateSchema,
  chatMessageUpdateSchema,
  diffOperationCreateSchema,
  diffOperationUpdateSchema,
  geoLocationCreateSchema,
  geoLocationUpdateSchema,
  languageStringCreateSchema,
  languageStringUpdateSchema,
  numberRangeCreateSchema,
  numberRangeUpdateSchema,
  orderCreateSchema,
  orderProductCreateSchema,
  orderProductMetaDataCreateSchema,
  orderProductMetaDataUpdateSchema,
  paymentIntentMetaDataCreateSchema,
  paymentIntentMetaDataUpdateSchema,
  paymentIntentUpdateSchema,
  productCreateSchema,
  productModerationCreateSchema,
  productModerationUpdateSchema,
  productReviewCreateSchema,
  productReviewUpdateSchema,
  productUpdateSchema,
  productVariationCreateSchema,
  productVariationMetadataCreateSchema,
  productVariationMetadataUpdateSchema,
  productVariationUpdateSchema,
  shippingExternalLocationCreateSchema,
  shippingExternalLocationUpdateSchema,
  subOrderCreateSchema,
  subOrderStatusEventCreateSchema,
  subOrderStatusEventUpdateSchema,
  subOrderUpdateSchema,
  userAddressCreateSchema,
  userAddressUpdateSchema,
  userUpdateSchema,
  variationAttributeCreateSchema,
  variationAttributeUpdateSchema,
  vendorCreateSchema,
  vendorModerationCreateSchema,
  vendorModerationUpdateSchema,
  vendorUpdateSchema,
  walletTransactionUpdateSchema,
  walletUpdateSchema,
} from '@libs/mtjr/shared/generated/types';

import Decimal from 'decimal.js';

export function getDirtyValues(form: FormGroup): Record<string, unknown> {
  const dirty: Record<string, unknown> = {};

  for (const key of Object.keys(form.controls)) {
    const control = form.controls[key];
    if (control instanceof FormGroup) {
      const child = getDirtyValues(control);
      if (Object.keys(child).length) {
        dirty[key] = child;
      }
    } else if (control.dirty) {
      dirty[key] = control.value;
    }
  }

  return dirty;
}

export function minDecimalValidator(minValue: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
    try {
      const decimalValue = new Decimal(value);
      const minDecimal = new Decimal(minValue);
      if (decimalValue.lessThan(minDecimal)) {
        return { min: { min: minValue, actual: value } };
      }
      return null;
    } catch {
      return { min: { min: minValue, actual: value } }; // Mimic 'min' error
    }
  };
}

export function maxDecimalValidator(maxValue: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
    try {
      const decimalValue = new Decimal(value);
      const maxDecimal = new Decimal(maxValue);
      if (decimalValue.greaterThan(maxDecimal)) {
        return { max: { max: maxValue, actual: value } };
      }
      return null;
    } catch {
      return { max: { max: maxValue, actual: value } };
    }
  };
}

export function diffOperationCreateUtils(fb: NonNullableFormBuilder, diffOperationCreate?: Entity.DiffOperationCreate) {
  const schema = diffOperationCreateSchema();
  const form = fb.group({
    op: fb.control(diffOperationCreate?.op, [Validators.required]),
    path: fb.control(diffOperationCreate?.path, [Validators.required]),
    value: fb.control(diffOperationCreate?.value, []),
    from: fb.control(diffOperationCreate?.from, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function diffOperationUpdateUtils(fb: NonNullableFormBuilder, diffOperationUpdate?: Entity.DiffOperationUpdate) {
  const schema = diffOperationUpdateSchema();
  const form = fb.group({
    op: fb.control(diffOperationUpdate?.op, []),
    path: fb.control(diffOperationUpdate?.path, []),
    value: fb.control(diffOperationUpdate?.value, []),
    from: fb.control(diffOperationUpdate?.from, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function languageStringCreateUtils(
  fb: NonNullableFormBuilder,
  languageStringCreate?: Entity.LanguageStringCreate,
) {
  const schema = languageStringCreateSchema();
  const form = fb.group({
    en: fb.control(languageStringCreate?.en, [Validators.required]),
    ar: fb.control(languageStringCreate?.ar, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function languageStringUpdateUtils(
  fb: NonNullableFormBuilder,
  languageStringUpdate?: Entity.LanguageStringUpdate,
) {
  const schema = languageStringUpdateSchema();
  const form = fb.group({
    en: fb.control(languageStringUpdate?.en, []),
    ar: fb.control(languageStringUpdate?.ar, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function geoLocationCreateUtils(fb: NonNullableFormBuilder, geoLocationCreate?: Entity.GeoLocationCreate) {
  const schema = geoLocationCreateSchema();
  const form = fb.group({
    latitude: fb.control(geoLocationCreate?.latitude, []),
    longitude: fb.control(geoLocationCreate?.longitude, []),
    name: fb.control(geoLocationCreate?.name, []),
    displayName: fb.control(geoLocationCreate?.displayName, []),
    type: fb.control(geoLocationCreate?.type, []),
    addressType: fb.control(geoLocationCreate?.addressType, []),
    importance: fb.control(geoLocationCreate?.importance, []),
    placeRank: fb.control(geoLocationCreate?.placeRank, []),
    noResult: fb.control(geoLocationCreate?.noResult, [Validators.required]),
    query: fb.control(geoLocationCreate?.query, [Validators.required]),
    shippingCompany: fb.control(geoLocationCreate?.shippingCompany, [Validators.required]),
    location: fb.control(geoLocationCreate?.location, [Validators.required, objectValidator(schema.shape.location)]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function geoLocationUpdateUtils(fb: NonNullableFormBuilder, geoLocationUpdate?: Entity.GeoLocationUpdate) {
  const schema = geoLocationUpdateSchema();
  const form = fb.group({
    latitude: fb.control(geoLocationUpdate?.latitude, []),
    longitude: fb.control(geoLocationUpdate?.longitude, []),
    name: fb.control(geoLocationUpdate?.name, []),
    displayName: fb.control(geoLocationUpdate?.displayName, []),
    type: fb.control(geoLocationUpdate?.type, []),
    addressType: fb.control(geoLocationUpdate?.addressType, []),
    importance: fb.control(geoLocationUpdate?.importance, []),
    placeRank: fb.control(geoLocationUpdate?.placeRank, []),
    noResult: fb.control(geoLocationUpdate?.noResult, []),
    query: fb.control(geoLocationUpdate?.query, []),
    shippingCompany: fb.control(geoLocationUpdate?.shippingCompany, []),
    location: fb.control(geoLocationUpdate?.location, [objectValidatorOptional(schema.shape.location)]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function shippingExternalLocationCreateUtils(
  fb: NonNullableFormBuilder,
  shippingExternalLocationCreate?: Entity.ShippingExternalLocationCreate,
) {
  const schema = shippingExternalLocationCreateSchema();
  const form = fb.group({
    cityId: fb.control(shippingExternalLocationCreate?.cityId, [Validators.required]),
    cityName: fb.control(shippingExternalLocationCreate?.cityName, [Validators.required]),
    regionId: fb.control(shippingExternalLocationCreate?.regionId, [Validators.required]),
    regionName: fb.control(shippingExternalLocationCreate?.regionName, [Validators.required]),
    regionRequired: fb.control(shippingExternalLocationCreate?.regionRequired, []),
    missing: fb.control(shippingExternalLocationCreate?.missing, [Validators.required]),
    shippingCompany: fb.control(shippingExternalLocationCreate?.shippingCompany, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function shippingExternalLocationUpdateUtils(
  fb: NonNullableFormBuilder,
  shippingExternalLocationUpdate?: Entity.ShippingExternalLocationUpdate,
) {
  const schema = shippingExternalLocationUpdateSchema();
  const form = fb.group({
    cityId: fb.control(shippingExternalLocationUpdate?.cityId, []),
    cityName: fb.control(shippingExternalLocationUpdate?.cityName, []),
    regionId: fb.control(shippingExternalLocationUpdate?.regionId, []),
    regionName: fb.control(shippingExternalLocationUpdate?.regionName, []),
    regionRequired: fb.control(shippingExternalLocationUpdate?.regionRequired, []),
    missing: fb.control(shippingExternalLocationUpdate?.missing, []),
    shippingCompany: fb.control(shippingExternalLocationUpdate?.shippingCompany, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function vendorCreateUtils(fb: NonNullableFormBuilder, vendorCreate?: Entity.VendorCreate) {
  const schema = vendorCreateSchema();
  const form = fb.group({
    deleted: fb.control(vendorCreate?.deleted, [Validators.required]),
    name: fb.control(vendorCreate?.name, [Validators.required]),
    addressLabel: fb.control(vendorCreate?.addressLabel, []),
    addressStreet: fb.control(vendorCreate?.addressStreet, []),
    addressInternal: fb.control(vendorCreate?.addressInternal, []),
    addressPhone: fb.control(vendorCreate?.addressPhone, []),
    addressPhone2: fb.control(vendorCreate?.addressPhone2, []),
    addressLat: fb.control(vendorCreate?.addressLat, []),
    addressLng: fb.control(vendorCreate?.addressLng, []),
    logo: fb.control(vendorCreate?.logo, []),
    background: fb.control(vendorCreate?.background, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function vendorUpdateUtils(fb: NonNullableFormBuilder, vendorUpdate?: Entity.VendorUpdate) {
  const schema = vendorUpdateSchema();
  const form = fb.group({
    deleted: fb.control(vendorUpdate?.deleted, []),
    name: fb.control(vendorUpdate?.name, []),
    addressLabel: fb.control(vendorUpdate?.addressLabel, []),
    addressStreet: fb.control(vendorUpdate?.addressStreet, []),
    addressInternal: fb.control(vendorUpdate?.addressInternal, []),
    addressPhone: fb.control(vendorUpdate?.addressPhone, []),
    addressPhone2: fb.control(vendorUpdate?.addressPhone2, []),
    addressLat: fb.control(vendorUpdate?.addressLat, []),
    addressLng: fb.control(vendorUpdate?.addressLng, []),
    logo: fb.control(vendorUpdate?.logo, []),
    background: fb.control(vendorUpdate?.background, []),
    allowedPaymentMethods: fb.control(vendorUpdate?.allowedPaymentMethods, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function chatMessageCreateUtils(fb: NonNullableFormBuilder, chatMessageCreate?: Entity.ChatMessageCreate) {
  const schema = chatMessageCreateSchema();
  const form = fb.group({
    text: fb.control(chatMessageCreate?.text, [Validators.required]),
    attachment: fb.control(chatMessageCreate?.attachment, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function chatMessageUpdateUtils(fb: NonNullableFormBuilder, chatMessageUpdate?: Entity.ChatMessageUpdate) {
  const schema = chatMessageUpdateSchema();
  const form = fb.group({
    text: fb.control(chatMessageUpdate?.text, []),
    attachment: fb.control(chatMessageUpdate?.attachment, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function chatMessageReadCreateUtils(
  fb: NonNullableFormBuilder,
  chatMessageReadCreate?: Entity.ChatMessageReadCreate,
) {
  const schema = chatMessageReadCreateSchema();
  const form = fb.group({
    messageId: fb.control(chatMessageReadCreate?.messageId, [Validators.required]),
    userId: fb.control(chatMessageReadCreate?.userId, [Validators.required]),
    readAt: fb.control(chatMessageReadCreate?.readAt, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function chatMessageReadUpdateUtils(
  fb: NonNullableFormBuilder,
  chatMessageReadUpdate?: Entity.ChatMessageReadUpdate,
) {
  const schema = chatMessageReadUpdateSchema();
  const form = fb.group({
    messageId: fb.control(chatMessageReadUpdate?.messageId, []),
    userId: fb.control(chatMessageReadUpdate?.userId, []),
    readAt: fb.control(chatMessageReadUpdate?.readAt, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function vendorModerationCreateUtils(
  fb: NonNullableFormBuilder,
  vendorModerationCreate?: Entity.VendorModerationCreate,
) {
  const schema = vendorModerationCreateSchema();
  const form = fb.group({
    vendorId: fb.control(vendorModerationCreate?.vendorId, [Validators.required]),
    decision: fb.control(vendorModerationCreate?.decision, [Validators.required]),
    reviewedById: fb.control(vendorModerationCreate?.reviewedById, []),
    reviewedAt: fb.control(vendorModerationCreate?.reviewedAt, []),
    notes: fb.control(vendorModerationCreate?.notes, []),
    logoCheck: fb.control(vendorModerationCreate?.logoCheck, []),
    backgroundCheck: fb.control(vendorModerationCreate?.backgroundCheck, []),
    profanityCheck: fb.control(vendorModerationCreate?.profanityCheck, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function vendorModerationUpdateUtils(
  fb: NonNullableFormBuilder,
  vendorModerationUpdate?: Entity.VendorModerationUpdate,
) {
  const schema = vendorModerationUpdateSchema();
  const form = fb.group({
    vendorId: fb.control(vendorModerationUpdate?.vendorId, []),
    decision: fb.control(vendorModerationUpdate?.decision, []),
    reviewedById: fb.control(vendorModerationUpdate?.reviewedById, []),
    reviewedAt: fb.control(vendorModerationUpdate?.reviewedAt, []),
    notes: fb.control(vendorModerationUpdate?.notes, []),
    logoCheck: fb.control(vendorModerationUpdate?.logoCheck, []),
    backgroundCheck: fb.control(vendorModerationUpdate?.backgroundCheck, []),
    profanityCheck: fb.control(vendorModerationUpdate?.profanityCheck, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productModerationCreateUtils(
  fb: NonNullableFormBuilder,
  productModerationCreate?: Entity.ProductModerationCreate,
) {
  const schema = productModerationCreateSchema();
  const form = fb.group({
    productId: fb.control(productModerationCreate?.productId, [Validators.required]),
    decision: fb.control(productModerationCreate?.decision, [Validators.required]),
    reviewedById: fb.control(productModerationCreate?.reviewedById, []),
    reviewedAt: fb.control(productModerationCreate?.reviewedAt, []),
    notes: fb.control(productModerationCreate?.notes, []),
    textCheck: fb.control(productModerationCreate?.textCheck, []),
    imageChecks: fb.control(productModerationCreate?.imageChecks, []),
    profanityCheck: fb.control(productModerationCreate?.profanityCheck, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productModerationUpdateUtils(
  fb: NonNullableFormBuilder,
  productModerationUpdate?: Entity.ProductModerationUpdate,
) {
  const schema = productModerationUpdateSchema();
  const form = fb.group({
    productId: fb.control(productModerationUpdate?.productId, []),
    decision: fb.control(productModerationUpdate?.decision, []),
    reviewedById: fb.control(productModerationUpdate?.reviewedById, []),
    reviewedAt: fb.control(productModerationUpdate?.reviewedAt, []),
    notes: fb.control(productModerationUpdate?.notes, []),
    textCheck: fb.control(productModerationUpdate?.textCheck, []),
    imageChecks: fb.control(productModerationUpdate?.imageChecks, []),
    profanityCheck: fb.control(productModerationUpdate?.profanityCheck, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function subOrderStatusEventCreateUtils(
  fb: NonNullableFormBuilder,
  subOrderStatusEventCreate?: Entity.SubOrderStatusEventCreate,
) {
  const schema = subOrderStatusEventCreateSchema();
  const form = fb.group({
    subOrderId: fb.control(subOrderStatusEventCreate?.subOrderId, [Validators.required]),
    status: fb.control(subOrderStatusEventCreate?.status, [Validators.required]),
    reasonEn: fb.control(subOrderStatusEventCreate?.reasonEn, []),
    reasonAr: fb.control(subOrderStatusEventCreate?.reasonAr, []),
    changedById: fb.control(subOrderStatusEventCreate?.changedById, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function subOrderStatusEventUpdateUtils(
  fb: NonNullableFormBuilder,
  subOrderStatusEventUpdate?: Entity.SubOrderStatusEventUpdate,
) {
  const schema = subOrderStatusEventUpdateSchema();
  const form = fb.group({
    subOrderId: fb.control(subOrderStatusEventUpdate?.subOrderId, []),
    status: fb.control(subOrderStatusEventUpdate?.status, []),
    reasonEn: fb.control(subOrderStatusEventUpdate?.reasonEn, []),
    reasonAr: fb.control(subOrderStatusEventUpdate?.reasonAr, []),
    changedById: fb.control(subOrderStatusEventUpdate?.changedById, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function orderCreateUtils(fb: NonNullableFormBuilder, orderCreate?: Entity.OrderCreate) {
  const schema = orderCreateSchema();
  const form = fb.group({
    idempotencyKey: fb.control(orderCreate?.idempotencyKey, [Validators.required]),
    addressLabel: fb.control(orderCreate?.addressLabel, []),
    addressStreet: fb.control(orderCreate?.addressStreet, [Validators.required]),
    addressInternal: fb.control(orderCreate?.addressInternal, []),
    addressPhone: fb.control(orderCreate?.addressPhone, [Validators.required]),
    addressPhone2: fb.control(orderCreate?.addressPhone2, []),
    addressLat: fb.control(orderCreate?.addressLat, [Validators.required]),
    addressLng: fb.control(orderCreate?.addressLng, [Validators.required]),
    paymentMethod: fb.control(orderCreate?.paymentMethod, [Validators.required]),
    notes: fb.control(orderCreate?.notes, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function subOrderCreateUtils(fb: NonNullableFormBuilder, subOrderCreate?: Entity.SubOrderCreate) {
  const schema = subOrderCreateSchema();
  const form = fb.group({
    orderProducts: fb.control(subOrderCreate?.orderProducts, [
      requiredWithEmptyArray(),
      objectValidator(schema.shape.orderProducts),
    ]),
    notesEn: fb.control(subOrderCreate?.notesEn, []),
    notesAr: fb.control(subOrderCreate?.notesAr, []),
    idempotencyKey: fb.control(subOrderCreate?.idempotencyKey, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function subOrderUpdateUtils(fb: NonNullableFormBuilder, subOrderUpdate?: Entity.SubOrderUpdate) {
  const schema = subOrderUpdateSchema();
  const form = fb.group({
    orderProducts: fb.control(subOrderUpdate?.orderProducts, [objectValidatorOptional(schema.shape.orderProducts)]),
    notesEn: fb.control(subOrderUpdate?.notesEn, []),
    notesAr: fb.control(subOrderUpdate?.notesAr, []),
    idempotencyKey: fb.control(subOrderUpdate?.idempotencyKey, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function paymentIntentMetaDataCreateUtils(
  fb: NonNullableFormBuilder,
  paymentIntentMetaDataCreate?: Entity.PaymentIntentMetaDataCreate,
) {
  const schema = paymentIntentMetaDataCreateSchema();
  const form = fb.group({
    transactionId: fb.control(paymentIntentMetaDataCreate?.transactionId, []),
    orderId: fb.control(paymentIntentMetaDataCreate?.orderId, []),
    customerId: fb.control(paymentIntentMetaDataCreate?.customerId, []),
    customerEmail: fb.control(paymentIntentMetaDataCreate?.customerEmail, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function paymentIntentMetaDataUpdateUtils(
  fb: NonNullableFormBuilder,
  paymentIntentMetaDataUpdate?: Entity.PaymentIntentMetaDataUpdate,
) {
  const schema = paymentIntentMetaDataUpdateSchema();
  const form = fb.group({
    transactionId: fb.control(paymentIntentMetaDataUpdate?.transactionId, []),
    orderId: fb.control(paymentIntentMetaDataUpdate?.orderId, []),
    customerId: fb.control(paymentIntentMetaDataUpdate?.customerId, []),
    customerEmail: fb.control(paymentIntentMetaDataUpdate?.customerEmail, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productVariationMetadataCreateUtils(
  fb: NonNullableFormBuilder,
  productVariationMetadataCreate?: Entity.ProductVariationMetadataCreate,
) {
  const schema = productVariationMetadataCreateSchema();
  const form = fb.group({
    voucherMno: fb.control(productVariationMetadataCreate?.voucherMno, []),
    voucherProductCode: fb.control(productVariationMetadataCreate?.voucherProductCode, []),
    voucherAmount: fb.control(productVariationMetadataCreate?.voucherAmount, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productVariationMetadataUpdateUtils(
  fb: NonNullableFormBuilder,
  productVariationMetadataUpdate?: Entity.ProductVariationMetadataUpdate,
) {
  const schema = productVariationMetadataUpdateSchema();
  const form = fb.group({
    voucherMno: fb.control(productVariationMetadataUpdate?.voucherMno, []),
    voucherProductCode: fb.control(productVariationMetadataUpdate?.voucherProductCode, []),
    voucherAmount: fb.control(productVariationMetadataUpdate?.voucherAmount, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productVariationCreateUtils(
  fb: NonNullableFormBuilder,
  productVariationCreate?: Entity.ProductVariationCreate,
) {
  const schema = productVariationCreateSchema();
  const form = fb.group({
    image: fb.control(productVariationCreate?.image, []),
    price: fb.control(productVariationCreate?.price, [Validators.required]),
    discount: fb.control(productVariationCreate?.discount, []),
    stock: fb.control(productVariationCreate?.stock, [Validators.required]),
    attributes: fb.control(productVariationCreate?.attributes, [
      requiredWithEmptyArray(),
      objectValidator(schema.shape.attributes),
    ]),
    metadata: fb.control(productVariationCreate?.metadata, [objectValidatorOptional(schema.shape.metadata)]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productVariationUpdateUtils(
  fb: NonNullableFormBuilder,
  productVariationUpdate?: Entity.ProductVariationUpdate,
) {
  const schema = productVariationUpdateSchema();
  const form = fb.group({
    image: fb.control(productVariationUpdate?.image, []),
    price: fb.control(productVariationUpdate?.price, []),
    discount: fb.control(productVariationUpdate?.discount, []),
    stock: fb.control(productVariationUpdate?.stock, []),
    attributes: fb.control(productVariationUpdate?.attributes, [objectValidatorOptional(schema.shape.attributes)]),
    metadata: fb.control(productVariationUpdate?.metadata, [objectValidatorOptional(schema.shape.metadata)]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function orderProductCreateUtils(fb: NonNullableFormBuilder, orderProductCreate?: Entity.OrderProductCreate) {
  const schema = orderProductCreateSchema();
  const form = fb.group({
    productId: fb.control(orderProductCreate?.productId, [Validators.required]),
    variationId: fb.control(orderProductCreate?.variationId, []),
    productName: fb.control(orderProductCreate?.productName, [Validators.required]),
    productImage: fb.control(orderProductCreate?.productImage, []),
    productType: fb.control(orderProductCreate?.productType, [Validators.required]),
    productSize: fb.control(orderProductCreate?.productSize, [Validators.required]),
    categoryId: fb.control(orderProductCreate?.categoryId, [Validators.required]),
    categoryName: fb.control(orderProductCreate?.categoryName, [Validators.required]),
    vendorId: fb.control(orderProductCreate?.vendorId, [Validators.required]),
    vendorName: fb.control(orderProductCreate?.vendorName, [Validators.required]),
    vendorLogo: fb.control(orderProductCreate?.vendorLogo, []),
    variationImage: fb.control(orderProductCreate?.variationImage, []),
    variationAttributes: fb.control(orderProductCreate?.variationAttributes, []),
    quantity: fb.control(orderProductCreate?.quantity, [Validators.required]),
    finalPrice: fb.control(orderProductCreate?.finalPrice, [Validators.required]),
    metadata: fb.control(orderProductCreate?.metadata, [objectValidatorOptional(schema.shape.metadata)]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function orderProductMetaDataCreateUtils(
  fb: NonNullableFormBuilder,
  orderProductMetaDataCreate?: Entity.OrderProductMetaDataCreate,
) {
  const schema = orderProductMetaDataCreateSchema();
  const form = fb.group({
    voucher: fb.control(orderProductMetaDataCreate?.voucher, [objectValidatorOptional(schema.shape.voucher)]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function orderProductMetaDataUpdateUtils(
  fb: NonNullableFormBuilder,
  orderProductMetaDataUpdate?: Entity.OrderProductMetaDataUpdate,
) {
  const schema = orderProductMetaDataUpdateSchema();
  const form = fb.group({
    voucher: fb.control(orderProductMetaDataUpdate?.voucher, [objectValidatorOptional(schema.shape.voucher)]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function categoryCreateUtils(fb: NonNullableFormBuilder, categoryCreate?: Entity.CategoryCreate) {
  const schema = categoryCreateSchema();
  const form = fb.group({
    name: fb.control(categoryCreate?.name, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function categoryUpdateUtils(fb: NonNullableFormBuilder, categoryUpdate?: Entity.CategoryUpdate) {
  const schema = categoryUpdateSchema();
  const form = fb.group({
    name: fb.control(categoryUpdate?.name, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function numberRangeCreateUtils(fb: NonNullableFormBuilder, numberRangeCreate?: Entity.NumberRangeCreate) {
  const schema = numberRangeCreateSchema();
  const form = fb.group({
    min: fb.control(numberRangeCreate?.min, [Validators.required]),
    max: fb.control(numberRangeCreate?.max, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function numberRangeUpdateUtils(fb: NonNullableFormBuilder, numberRangeUpdate?: Entity.NumberRangeUpdate) {
  const schema = numberRangeUpdateSchema();
  const form = fb.group({
    min: fb.control(numberRangeUpdate?.min, []),
    max: fb.control(numberRangeUpdate?.max, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productCreateUtils(fb: NonNullableFormBuilder, productCreate?: Entity.ProductCreate) {
  const schema = productCreateSchema();
  const form = fb.group({
    size: fb.control(productCreate?.size, [Validators.required]),
    name: fb.control(productCreate?.name, [Validators.required]),
    description: fb.control(productCreate?.description, []),
    images: fb.control(productCreate?.images, []),
    price: fb.control(productCreate?.price, []),
    discount: fb.control(productCreate?.discount, []),
    stock: fb.control(productCreate?.stock, []),
    variations: fb.control(productCreate?.variations, [objectValidatorOptional(schema.shape.variations)]),
    bgColor: fb.control(productCreate?.bgColor, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productUpdateUtils(fb: NonNullableFormBuilder, productUpdate?: Entity.ProductUpdate) {
  const schema = productUpdateSchema();
  const form = fb.group({
    size: fb.control(productUpdate?.size, []),
    name: fb.control(productUpdate?.name, []),
    description: fb.control(productUpdate?.description, []),
    images: fb.control(productUpdate?.images, []),
    price: fb.control(productUpdate?.price, []),
    discount: fb.control(productUpdate?.discount, []),
    stock: fb.control(productUpdate?.stock, []),
    variations: fb.control(productUpdate?.variations, [objectValidatorOptional(schema.shape.variations)]),
    bgColor: fb.control(productUpdate?.bgColor, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function variationAttributeCreateUtils(
  fb: NonNullableFormBuilder,
  variationAttributeCreate?: Entity.VariationAttributeCreate,
) {
  const schema = variationAttributeCreateSchema();
  const form = fb.group({
    typeLabel: fb.control(variationAttributeCreate?.typeLabel, [Validators.required]),
    typeKey: fb.control(variationAttributeCreate?.typeKey, [Validators.required]),
    type: fb.control(variationAttributeCreate?.type, [Validators.required]),
    label: fb.control(variationAttributeCreate?.label, [Validators.required]),
    key: fb.control(variationAttributeCreate?.key, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function variationAttributeUpdateUtils(
  fb: NonNullableFormBuilder,
  variationAttributeUpdate?: Entity.VariationAttributeUpdate,
) {
  const schema = variationAttributeUpdateSchema();
  const form = fb.group({
    typeLabel: fb.control(variationAttributeUpdate?.typeLabel, []),
    typeKey: fb.control(variationAttributeUpdate?.typeKey, []),
    type: fb.control(variationAttributeUpdate?.type, []),
    label: fb.control(variationAttributeUpdate?.label, []),
    key: fb.control(variationAttributeUpdate?.key, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function attachmentCreateUtils(fb: NonNullableFormBuilder, attachmentCreate?: Entity.AttachmentCreate) {
  const schema = attachmentCreateSchema();
  const form = fb.group({
    description: fb.control(attachmentCreate?.description, []),
    url: fb.control(attachmentCreate?.url, [Validators.required]),
    key: fb.control(attachmentCreate?.key, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function attachmentUpdateUtils(fb: NonNullableFormBuilder, attachmentUpdate?: Entity.AttachmentUpdate) {
  const schema = attachmentUpdateSchema();
  const form = fb.group({
    description: fb.control(attachmentUpdate?.description, []),
    url: fb.control(attachmentUpdate?.url, []),
    key: fb.control(attachmentUpdate?.key, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productReviewCreateUtils(fb: NonNullableFormBuilder, productReviewCreate?: Entity.ProductReviewCreate) {
  const schema = productReviewCreateSchema();
  const form = fb.group({
    orderId: fb.control(productReviewCreate?.orderId, [Validators.required]),
    subOrderId: fb.control(productReviewCreate?.subOrderId, [Validators.required]),
    productId: fb.control(productReviewCreate?.productId, [Validators.required]),
    rating: fb.control(productReviewCreate?.rating, [Validators.required]),
    review: fb.control(productReviewCreate?.review, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function productReviewUpdateUtils(fb: NonNullableFormBuilder, productReviewUpdate?: Entity.ProductReviewUpdate) {
  const schema = productReviewUpdateSchema();
  const form = fb.group({
    orderId: fb.control(productReviewUpdate?.orderId, []),
    subOrderId: fb.control(productReviewUpdate?.subOrderId, []),
    productId: fb.control(productReviewUpdate?.productId, []),
    rating: fb.control(productReviewUpdate?.rating, []),
    review: fb.control(productReviewUpdate?.review, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function addressCreateUtils(fb: NonNullableFormBuilder, addressCreate?: Entity.AddressCreate) {
  const schema = addressCreateSchema();
  const form = fb.group({
    label: fb.control(addressCreate?.label, [Validators.required]),
    street: fb.control(addressCreate?.street, [Validators.required]),
    internalAddress: fb.control(addressCreate?.internalAddress, []),
    phone: fb.control(addressCreate?.phone, [Validators.required]),
    phone2: fb.control(addressCreate?.phone2, []),
    latitude: fb.control(addressCreate?.latitude, [Validators.required]),
    longitude: fb.control(addressCreate?.longitude, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function addressUpdateUtils(fb: NonNullableFormBuilder, addressUpdate?: Entity.AddressUpdate) {
  const schema = addressUpdateSchema();
  const form = fb.group({
    label: fb.control(addressUpdate?.label, []),
    street: fb.control(addressUpdate?.street, []),
    internalAddress: fb.control(addressUpdate?.internalAddress, []),
    phone: fb.control(addressUpdate?.phone, []),
    phone2: fb.control(addressUpdate?.phone2, []),
    latitude: fb.control(addressUpdate?.latitude, []),
    longitude: fb.control(addressUpdate?.longitude, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function userAddressCreateUtils(fb: NonNullableFormBuilder, userAddressCreate?: Entity.UserAddressCreate) {
  const schema = userAddressCreateSchema();
  const form = fb.group({
    label: fb.control(userAddressCreate?.label, [Validators.required]),
    street: fb.control(userAddressCreate?.street, [Validators.required]),
    internalAddress: fb.control(userAddressCreate?.internalAddress, []),
    phone: fb.control(userAddressCreate?.phone, [Validators.required]),
    phone2: fb.control(userAddressCreate?.phone2, []),
    latitude: fb.control(userAddressCreate?.latitude, [Validators.required]),
    longitude: fb.control(userAddressCreate?.longitude, [Validators.required]),
    isDefault: fb.control(userAddressCreate?.isDefault, [Validators.required]),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function userAddressUpdateUtils(fb: NonNullableFormBuilder, userAddressUpdate?: Entity.UserAddressUpdate) {
  const schema = userAddressUpdateSchema();
  const form = fb.group({
    label: fb.control(userAddressUpdate?.label, []),
    street: fb.control(userAddressUpdate?.street, []),
    internalAddress: fb.control(userAddressUpdate?.internalAddress, []),
    phone: fb.control(userAddressUpdate?.phone, []),
    phone2: fb.control(userAddressUpdate?.phone2, []),
    latitude: fb.control(userAddressUpdate?.latitude, []),
    longitude: fb.control(userAddressUpdate?.longitude, []),
    isDefault: fb.control(userAddressUpdate?.isDefault, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function userUpdateUtils(fb: NonNullableFormBuilder, userUpdate?: Entity.UserUpdate) {
  const schema = userUpdateSchema();
  const form = fb.group({
    authId: fb.control(userUpdate?.authId, []),
    name: fb.control(userUpdate?.name, []),
    phone: fb.control(userUpdate?.phone, []),
    avatar: fb.control(userUpdate?.avatar, []),
    isAdmin: fb.control(userUpdate?.isAdmin, []),
    birthdate: fb.control(userUpdate?.birthdate, []),
    vendorIds: fb.control(userUpdate?.vendorIds, []),
    customerFcmTokens: fb.control(userUpdate?.customerFcmTokens, []),
    adminFcmTokens: fb.control(userUpdate?.adminFcmTokens, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function walletTransactionUpdateUtils(
  fb: NonNullableFormBuilder,
  walletTransactionUpdate?: Entity.WalletTransactionUpdate,
) {
  const schema = walletTransactionUpdateSchema();
  const form = fb.group({
    walletId: fb.control(walletTransactionUpdate?.walletId, []),
    amount: fb.control(walletTransactionUpdate?.amount, []),
    currency: fb.control(walletTransactionUpdate?.currency, []),
    type: fb.control(walletTransactionUpdate?.type, []),
    status: fb.control(walletTransactionUpdate?.status, []),
    idempotencyKey: fb.control(walletTransactionUpdate?.idempotencyKey, []),
    provider: fb.control(walletTransactionUpdate?.provider, []),
    providerIntent: fb.control(walletTransactionUpdate?.providerIntent, []),
    confirmedAt: fb.control(walletTransactionUpdate?.confirmedAt, []),
    orgId: fb.control(walletTransactionUpdate?.orgId, []),
    providerPayout: fb.control(walletTransactionUpdate?.providerPayout, []),
    processedAt: fb.control(walletTransactionUpdate?.processedAt, []),
    topUpPaymentMethod: fb.control(walletTransactionUpdate?.topUpPaymentMethod, []),
    orderId: fb.control(walletTransactionUpdate?.orderId, []),
    subOrderId: fb.control(walletTransactionUpdate?.subOrderId, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function walletUpdateUtils(fb: NonNullableFormBuilder, walletUpdate?: Entity.WalletUpdate) {
  const schema = walletUpdateSchema();
  const form = fb.group({
    balance: fb.control(walletUpdate?.balance, []),
    holdBalance: fb.control(walletUpdate?.holdBalance, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}

export function paymentIntentUpdateUtils(fb: NonNullableFormBuilder, paymentIntentUpdate?: Entity.PaymentIntentUpdate) {
  const schema = paymentIntentUpdateSchema();
  const form = fb.group({
    amount: fb.control(paymentIntentUpdate?.amount, [Validators.min(1)]),
    topUpPaymentMethod: fb.control(paymentIntentUpdate?.topUpPaymentMethod, []),
    identityCard: fb.control(paymentIntentUpdate?.identityCard, []),
    masaratSessionToken: fb.control(paymentIntentUpdate?.masaratSessionToken, []),
  });
  return {
    form,
    schema,
    parse: () => schema.safeParse(form.value),
  };
}
